'use client'

import * as React from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { useQuery } from '@tanstack/react-query'
import { Check, Copy } from 'lucide-react'
import { z } from 'zod/v4'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Skeleton,
  Textarea,
} from '@ui/web/components'
import { FormattedPriceInput } from '@ui/web/lib'
import { tryCatch } from '@helper/utils/core'
import { formatCurrency, getInitials } from '@helper/utils/format'
import { useHandleCopy } from '@helper/web/hooks'

import { useSharedTRPC } from '../../context/trpc'

const PayWithdrawalSchema = z.object({
  amount: z.number().positive('Amount must be positive'),
  notes: z.string().optional(),
  receiptFile: z.instanceof(File).optional(),
})

export type PayWithdrawalValues = z.infer<typeof PayWithdrawalSchema>

export interface PayWithdrawalDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentBalance: number
  withdrawalId: string
  defaultAmount: number
  currency: string
  onPayWithdrawal: (
    value: PayWithdrawalValues & { withdrawalId: string; defaultAmount: number },
  ) => Promise<void> | void
  onSuccess?: () => void
}

export function PayWithdrawalDialog({
  open,
  onOpenChange,
  currentBalance,
  withdrawalId,
  defaultAmount,
  currency,
  onSuccess,
  onPayWithdrawal,
}: PayWithdrawalDialogProps) {
  const trpc = useSharedTRPC()

  const { isCopied, handleCopy } = useHandleCopy()
  const { copy: isCopiedAll, handleCopy: handleCopyAll } = useHandleCopy()

  const [showConfirm, setShowConfirm] = React.useState(false)
  const [receiptFile, setReceiptFile] = React.useState<File | null>(null)
  const [uploadError, setUploadError] = React.useState<string | null>(null)
  const [formValues, setFormValues] = React.useState<PayWithdrawalValues | null>(null)

  const { data: withdrawalDetails, isPending: isPendingDetails } = useQuery({
    ...trpc.royalty.getWithdrawalForPayment.queryOptions({ withdrawalId }),
    staleTime: 1000 * 60 * 5,
    enabled: open && Boolean(withdrawalId),
  })

  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const form = useForm({
    defaultValues: {
      amount: defaultAmount,
      notes: undefined,
      receiptFile: undefined,
    } as z.input<typeof PayWithdrawalSchema>,
    validators: {
      onSubmit: PayWithdrawalSchema,
      onBlur: PayWithdrawalSchema,
      onChange: PayWithdrawalSchema,
    },
    onSubmit: ({ formApi, value }) => {
      if (!receiptFile) {
        formApi.setErrorMap({
          // @ts-expect-error - form-level error as string
          onSubmit: 'Payment receipt wajib dilampirkan',
        })
        return
      }

      if (value.amount !== defaultAmount && value.amount > currentBalance + defaultAmount) {
        formApi.setErrorMap({
          // @ts-expect-error - form-level error as string
          onSubmit: `Jumlah yang akan dibayar lebih besar dari saldo saat ini. Saldo saat ini: ${formatCurrency(currentBalance + defaultAmount, { currency })}`,
        })
        return
      }

      const formValueWithFile: PayWithdrawalValues = {
        ...value,
        receiptFile: receiptFile,
      }

      setFormValues(formValueWithFile)
      setShowConfirm(true)
    },
  })

  const handleSendUpdate = async () => {
    await form.handleSubmit()
  }

  const handleConfirmSubmit = async () => {
    if (!formValues) {
      return
    }

    setIsSubmitting(true)
    setUploadError(null)

    try {
      const [_, error] = await tryCatch(
        async () => await onPayWithdrawal({ ...formValues, withdrawalId, defaultAmount }),
      )

      if (error) {
        setUploadError(error.message)
        setShowConfirm(false)
        form.setErrorMap({
          // @ts-expect-error - form-level error as string
          onSubmit: error.message,
        })
      } else {
        setShowConfirm(false)
        onOpenChange(false)
        onSuccess?.()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const errorMap = useStore(form.store, (state) => state.errorMap)
  const formIsSubmitting = useStore(form.store, (state) => state.isSubmitting)

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(open) => {
          onOpenChange(open)

          if (!open) {
            form.reset()
            setReceiptFile(null)
            setUploadError(null)
            setShowConfirm(false)
            setFormValues(null)
            setIsSubmitting(false)
          }
        }}
      >
        <DialogContent className='srd:max-w-md'>
          <DialogHeader>
            <DialogTitle>Update Withdrawal Request</DialogTitle>
          </DialogHeader>

          {isPendingDetails || !withdrawalDetails ? (
            <div className='srd:space-y-4'>
              <Skeleton className='srd:w-full srd:h-10' />
              <Skeleton className='srd:w-full srd:h-10' />
              <Skeleton className='srd:w-full srd:h-10' />
            </div>
          ) : (
            <div className='srd:space-y-4'>
              {/* Publisher */}
              <div>
                <p className='srd:mb-1 srd:text-xs srd:font-medium srd:text-neutrals-n-75'>Publisher</p>
                <div className='srd:flex srd:items-center srd:gap-2 srd:text-complementary-black'>
                  <Avatar size='lg' profile='organization'>
                    <AvatarImage
                      src={withdrawalDetails.organizationLogo ?? undefined}
                      alt={withdrawalDetails.organizationName}
                    />
                    <AvatarFallback>{getInitials(withdrawalDetails.organizationName)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className='srd:font-medium'>{withdrawalDetails.organizationName}</p>
                    {withdrawalDetails.organizationEntityName && (
                      <p className='srd:text-sm srd:text-neutrals-n-75'>{withdrawalDetails.organizationEntityName}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div>
                <div className='srd:mb-2 srd:flex srd:items-center srd:justify-between srd:gap-2'>
                  <p className='srd:text-xs srd:font-medium srd:text-neutrals-n-75'>Payment Details</p>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => {
                      const allLines = [
                        withdrawalDetails.bankName != null ? `Bank Name: ${withdrawalDetails.bankName}` : null,
                        withdrawalDetails.accountNumber != null
                          ? `Account Number: ${withdrawalDetails.accountNumber}`
                          : null,
                        withdrawalDetails.accountHolderName != null
                          ? `Account Holder: ${withdrawalDetails.accountHolderName}`
                          : null,
                        withdrawalDetails.swiftCode != null ? `Swift Code: ${withdrawalDetails.swiftCode}` : null,
                        withdrawalDetails.bankBranch != null ? `Branch Name: ${withdrawalDetails.bankBranch}` : null,
                      ].filter(Boolean)
                      const allText = allLines.join('\n')
                      handleCopyAll(allText)
                    }}
                    disabled={isCopiedAll}
                    className='srd:gap-x-2 srd:text-xs srd:p-0 srd:h-fit'
                  >
                    {isCopiedAll ? <Check className='srd:size-3' /> : <Copy className='srd:size-3' />}
                    Copy All
                  </Button>
                </div>
                <div className='srd:grid srd:gap-x-6 srd:gap-y-1 srd:text-sm'>
                  {withdrawalDetails.bankName != null && (
                    <div className='srd:flex srd:items-center srd:gap-2'>
                      <p>
                        <span className='srd:text-neutrals-n-75'>Bank Name:</span>{' '}
                        <span className='srd:text-complementary-black'>{withdrawalDetails.bankName}</span>
                      </p>
                      <Button
                        variant='link'
                        className='srd:h-fit srd:w-fit srd:p-0'
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        onClick={() => handleCopy(withdrawalDetails.bankName!, 'bankName')}
                        disabled={isCopied('bankName')}
                      >
                        {isCopied('bankName') ? <Check className='srd:size-3' /> : <Copy className='srd:size-3' />}
                      </Button>
                    </div>
                  )}
                  {withdrawalDetails.accountNumber != null && (
                    <div className='srd:flex srd:items-center srd:gap-2'>
                      <p>
                        <span className='srd:text-neutrals-n-75'>Account Number:</span>{' '}
                        <span className='srd:text-complementary-black'>{withdrawalDetails.accountNumber}</span>
                      </p>
                      <Button
                        variant='link'
                        className='srd:h-fit srd:w-fit srd:p-0'
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        onClick={() => handleCopy(withdrawalDetails.accountNumber!, 'accountNumber')}
                        disabled={isCopied('accountNumber')}
                      >
                        {isCopied('accountNumber') ? <Check className='srd:size-3' /> : <Copy className='srd:size-3' />}
                      </Button>
                    </div>
                  )}
                  {withdrawalDetails.swiftCode != null && (
                    <div className='srd:flex srd:items-center srd:gap-2'>
                      <p>
                        <span className='srd:text-neutrals-n-75'>Swift Code:</span>{' '}
                        <span className='srd:text-complementary-black'>{withdrawalDetails.swiftCode}</span>
                      </p>
                      <Button
                        variant='link'
                        className='srd:h-fit srd:w-fit srd:p-0'
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        onClick={() => handleCopy(withdrawalDetails.swiftCode!, 'swiftCode')}
                        disabled={isCopied('swiftCode')}
                      >
                        {isCopied('swiftCode') ? <Check className='srd:size-3' /> : <Copy className='srd:size-3' />}
                      </Button>
                    </div>
                  )}
                  {withdrawalDetails.bankBranch != null && (
                    <div className='srd:flex srd:items-center srd:gap-2'>
                      <p>
                        <span className='srd:text-neutrals-n-75'>Branch Name:</span>{' '}
                        <span className='srd:text-complementary-black'>{withdrawalDetails.bankBranch}</span>
                      </p>
                      <Button
                        variant='link'
                        className='srd:h-fit srd:w-fit srd:p-0'
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        onClick={() => handleCopy(withdrawalDetails.bankBranch!, 'bankBranch')}
                        disabled={isCopied('bankBranch')}
                      >
                        {isCopied('bankBranch') ? <Check className='srd:size-3' /> : <Copy className='srd:size-3' />}
                      </Button>
                    </div>
                  )}
                  {withdrawalDetails.accountHolderName != null && (
                    <div className='srd:flex srd:items-center srd:gap-2'>
                      <p>
                        <span className='srd:text-neutrals-n-75'>Account Holder:</span>{' '}
                        <span className='srd:text-complementary-black'>{withdrawalDetails.accountHolderName}</span>
                      </p>
                      <Button
                        variant='link'
                        className='srd:h-fit srd:w-fit srd:p-0'
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        onClick={() => handleCopy(withdrawalDetails.accountHolderName!, 'accountHolderName')}
                        disabled={isCopied('accountHolderName')}
                      >
                        {isCopied('accountHolderName') ? (
                          <Check className='srd:size-3' />
                        ) : (
                          <Copy className='srd:size-3' />
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <form
                id='pay-withdrawal-form'
                onSubmit={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  void handleSendUpdate()
                }}
                className='srd:space-y-4'
              >
                <form.Field name='amount'>
                  {(field) => (
                    <Field className='srd:gap-1'>
                      <FieldLabel htmlFor='pay-amount'>Amount (in {currency})</FieldLabel>
                      <FormattedPriceInput
                        id='pay-amount'
                        value={field.state.value}
                        onChange={(v) => field.handleChange(v)}
                        placeholder='0'
                        className='srd:w-full'
                        // disabled={form.state.isSubmitting || isSubmitting}
                        disabled
                        onBlur={field.handleBlur}
                      />
                      <FieldDescription>
                        Untuk saat ini, amount tidak bisa diubah (feature mengubah amount belum tersedia).
                      </FieldDescription>
                    </Field>
                  )}
                </form.Field>

                <form.Field name='notes'>
                  {(field) => (
                    <Field className='srd:gap-1'>
                      <FieldLabel htmlFor='pay-notes'>
                        Notes <Badge>Optional</Badge>
                      </FieldLabel>
                      <Textarea
                        id='pay-notes'
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        className='srd:min-h-20 srd:w-full'
                        disabled={formIsSubmitting || isSubmitting}
                        onBlur={field.handleBlur}
                      />
                    </Field>
                  )}
                </form.Field>

                <Field className='srd:gap-1'>
                  <FieldLabel htmlFor='pay-receipt'>Payment Receipt</FieldLabel>
                  <Input
                    id='pay-receipt'
                    type='file'
                    accept='image/*,.pdf'
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) {
                        setReceiptFile(f)
                        form.setFieldValue('receiptFile', f)
                        setUploadError(null)
                      }
                    }}
                    disabled={formIsSubmitting || isSubmitting}
                  />
                  <FieldDescription>Select a payment receipt to upload.</FieldDescription>
                </Field>

                {uploadError && (
                  <p className='srd:text-sm srd:text-main-red-100' role='alert'>
                    {uploadError}
                  </p>
                )}

                {errorMap?.onSubmit && typeof errorMap.onSubmit === 'string' ? (
                  <p className='srd:text-main-red-100 srd:mt-4 srd:text-xs'>{errorMap.onSubmit}</p>
                ) : null}
              </form>
            </div>
          )}

          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type='button'
              onClick={handleSendUpdate}
              disabled={isPendingDetails || !withdrawalDetails || !receiptFile || isSubmitting}
              loading={isSubmitting}
            >
              Send Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Yakin ubah status jadi lunas?</AlertDialogTitle>
          </AlertDialogHeader>

          <p className='srd:text-sm'>
            Mengunggah bukti transfer akan mengubah status permintaan penarikan royalti ini menjadi lunas dan tidak
            dapat diubah.
          </p>

          {formValues && formValues.amount !== defaultAmount && (
            <p className='srd:mt-2 srd:rounded-md srd:border srd:border-complementary-yellow-20 srd:bg-complementary-yellow-10 srd:p-2 srd:text-sm srd:text-complementary-black'>
              <strong className='srd:text-complementary-yellow-100'>Catatan:</strong> Jumlah yang akan dibayar (
              {formatCurrency(formValues.amount, { currency })}) berbeda dari jumlah yang diminta (
              {formatCurrency(defaultAmount, { currency })}).
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirm(false)} disabled={isSubmitting}>
              Tidak jadi
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Memproses...' : 'Yakin'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
